/**
 * MMOAmbientEntity - Display class for ambient entities (NPC traffic, hazards, static objects)
 * Uses sprite images with programmatic fallback.
 */

import Phaser from 'phaser';
import { axialToPixel, type HexCoord } from '../../shared/HexUtils';
import type { MMOAmbientEntityData } from '../../managers/MMOStateManager';

// Entity type -> sprite key mapping
const AMBIENT_SPRITE_MAP: Record<string, string> = {
  cargo_convoy: 'ambient-cargo-convoy',
  civilian_transport: 'ambient-civilian-transport',
  science_vessel: 'ambient-science-vessel',
  refugee_ship: 'ambient-refugee-ship',
  patrol_ship: 'ambient-patrol-ship',
  meteor: 'ambient-meteor',
  comet: 'ambient-comet',
  radiation_storm: 'ambient-radiation-storm',
  ion_storm_moving: 'ambient-ion-storm',
  derelict_ship: 'ambient-derelict-ship',
  memorial_beacon: 'ambient-memorial-beacon',
  debris_cluster: 'ambient-debris-cluster',
  treasure_wreck: 'ambient-derelict-ship',
  alien_artifact: 'ambient-memorial-beacon',
  asteroid_cluster: 'ambient-debris-cluster',
  enemy_spawn: 'ambient-patrol-ship',
};

// Display sizes per entity type (some are larger/smaller for visual variety)
const SPRITE_SIZES: Record<string, number> = {
  cargo_convoy: 36,
  civilian_transport: 36,
  science_vessel: 34,
  refugee_ship: 34,
  patrol_ship: 34,
  meteor: 28,
  comet: 38,
  radiation_storm: 52,
  ion_storm_moving: 48,
  derelict_ship: 32,
  memorial_beacon: 26,
  debris_cluster: 36,
  treasure_wreck: 36,
  alien_artifact: 30,
  asteroid_cluster: 38,
  enemy_spawn: 34,
};

export class MMOAmbientEntity extends Phaser.GameObjects.Container {
  public id: string;
  public entityType: string;
  public entitySubtype: string;
  public hexCoord: HexCoord;
  public displayName: string;
  public properties: Record<string, any>;
  public lootable: boolean;
  public scannable: boolean;
  public currentHp: number;
  public maxHp: number;
  public velocity: HexCoord;

  private entitySprite: Phaser.GameObjects.Image | null = null;
  private entityGraphics: Phaser.GameObjects.Graphics;
  private nameLabel: Phaser.GameObjects.Text;
  private selectionIndicator: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene, data: MMOAmbientEntityData) {
    const pixel = axialToPixel(data.position.q, data.position.r);
    super(scene, pixel.x, pixel.y);

    this.id = data.id;
    this.entityType = data.entityType;
    this.entitySubtype = data.entitySubtype || '';
    this.hexCoord = data.position;
    this.displayName = data.displayName;
    this.properties = data.properties || {};
    this.lootable = data.lootable;
    this.scannable = data.scannable;
    this.currentHp = data.currentHp;
    this.maxHp = data.maxHp;
    this.velocity = data.velocity || { q: 0, r: 0 };

    this.selectionIndicator = this.scene.add.graphics();
    this.selectionIndicator.setVisible(false);
    this.add(this.selectionIndicator);

    this.entityGraphics = this.scene.add.graphics();
    this.add(this.entityGraphics);

    this.nameLabel = this.scene.add.text(0, 22, '', {
      fontSize: '8px',
      color: '#aaaaaa',
    });
    this.nameLabel.setOrigin(0.5);
    this.nameLabel.setVisible(false);
    this.add(this.nameLabel);

    this.drawVisuals();
    this.setEntityDepth();

    this.setSize(40, 40);
    this.setInteractive(new Phaser.Geom.Rectangle(-20, -20, 40, 40), Phaser.Geom.Rectangle.Contains);

    // NOTE: No sprite-level 'pointerdown' handler — see MMOUnit.ts for rationale.
    // Selection is handled in MMOGameScene.onPointerUp via getEntityAtWorldPoint().
  }

  public updateFromServer(data: MMOAmbientEntityData): void {
    if (!this.scene || !this.scene.sys) return;

    this.entityType = data.entityType;
    this.entitySubtype = data.entitySubtype || '';
    this.displayName = data.displayName;
    this.properties = data.properties || {};
    this.lootable = data.lootable;
    this.scannable = data.scannable;
    this.currentHp = data.currentHp;
    this.maxHp = data.maxHp;
    this.velocity = data.velocity || { q: 0, r: 0 };

    if (data.position.q !== this.hexCoord.q || data.position.r !== this.hexCoord.r) {
      this.hexCoord = data.position;
      const pixel = axialToPixel(this.hexCoord.q, this.hexCoord.r);
      if (this.velocity.q !== 0 || this.velocity.r !== 0) {
        this.scene.tweens.add({
          targets: this,
          x: pixel.x,
          y: pixel.y,
          duration: 800,
          ease: 'Linear',
        });
      } else {
        this.setPosition(pixel.x, pixel.y);
      }
    }

    this.drawVisuals();
  }

  public setVisibility(fullyVisible: boolean, explored: boolean): void {
    if (!explored) {
      this.setAlpha(0);
    } else if (!fullyVisible) {
      this.setAlpha(0.3);
    } else {
      this.setAlpha(1);
    }
  }

  public setSelected(selected: boolean): void {
    this.selectionIndicator.setVisible(selected);
  }

  private setEntityDepth(): void {
    const isTraffic = ['cargo_convoy', 'civilian_transport', 'science_vessel', 'refugee_ship', 'patrol_ship'].includes(this.entityType);
    const isHazard = ['meteor', 'comet', 'radiation_storm', 'ion_storm_moving'].includes(this.entityType);

    if (isHazard) {
      this.setDepth(6);
    } else if (isTraffic) {
      this.setDepth(4);
    } else {
      this.setDepth(2);
    }
  }

  private drawVisuals(): void {
    this.entityGraphics.clear();

    const spriteKey = AMBIENT_SPRITE_MAP[this.entityType];
    if (spriteKey && this.scene.textures.exists(spriteKey)) {
      this.drawWithSprite(spriteKey);
    } else {
      this.drawFallback();
    }

    this.nameLabel.setText(this.displayName);

    this.selectionIndicator.clear();
    this.selectionIndicator.lineStyle(2, 0x44ff44, 1);
    this.selectionIndicator.strokeCircle(0, 0, 22);
  }

  private drawWithSprite(spriteKey: string): void {
    const size = SPRITE_SIZES[this.entityType] || 32;

    if (!this.entitySprite) {
      this.entitySprite = this.scene.add.image(0, 0, spriteKey);
      this.entitySprite.setDisplaySize(size, size);
      this.add(this.entitySprite);
      this.moveTo(this.entitySprite, 1);
    } else if (this.entitySprite.texture.key !== spriteKey) {
      this.entitySprite.setTexture(spriteKey);
      this.entitySprite.setDisplaySize(size, size);
    }

    // Hazard types get a subtle alpha pulse
    const isHazard = ['radiation_storm', 'ion_storm_moving'].includes(this.entityType);
    if (isHazard) {
      this.entitySprite.setAlpha(0.85);
    }
  }

  private drawFallback(): void {
    if (this.entitySprite) {
      this.entitySprite.setVisible(false);
    }
    const g = this.entityGraphics;
    const colorMap: Record<string, number> = {
      treasure_wreck: 0xf1c40f,
      alien_artifact: 0xe056fd,
      derelict_ship: 0x95a5a6,
      debris_cluster: 0x7f8c8d,
      memorial_beacon: 0x3498db,
      asteroid_cluster: 0xd35400,
      enemy_spawn: 0xe74c3c,
    };
    const color = colorMap[this.entityType] || 0xaaaaaa;
    const size = this.lootable || this.scannable ? 12 : 8;
    g.fillStyle(color, 0.7);
    g.fillCircle(0, 0, size);
    if (this.lootable || this.scannable) {
      g.lineStyle(1, 0xffffff, 0.5);
      g.strokeCircle(0, 0, size);
    }
  }

  public destroy(fromScene?: boolean): void {
    if (this.entitySprite) {
      this.entitySprite.destroy();
      this.entitySprite = null;
    }
    super.destroy(fromScene);
  }
}
